import type { RouteLocationNormalized } from 'vue-router';
import { CombinedGraphQLErrors } from '@apollo/client';
import { getFirstNestJsGraphQLError } from '@pcg/graphql-kit';

import { useErrorStore } from '@ohmybible/core';

import { i18n } from '@/core/i18n';
import { useAuthStore } from '@/stores/auth';

/**
 * Every page needs a session except the public ones. The session is an
 * HttpOnly cookie JS cannot see, so the guard asks the API (`me`): a Guest
 * answer means "sign in first". Readers and staff are both welcome — unlike
 * the admin, there is no role gate here.
 */
export const authMiddleware = async (to: RouteLocationNormalized) => {
  const auth = useAuthStore();

  if (to.meta.public) {
    if (!auth.user) {
      try {
        await auth.fetchMe();
      } catch {
        // A public page renders for a guest anyway.
      }
    }

    if (to.meta.guestOnly && auth.isSignedIn) {
      return { name: 'Home' };
    }

    return;
  }

  if (!auth.user) {
    try {
      await auth.fetchMe();
    } catch (error) {
      const statusCode = CombinedGraphQLErrors.is(error)
        ? getFirstNestJsGraphQLError(error)?.extensions.statusCode
        : undefined;

      // Not an auth failure (the error link already redirects those): the
      // API is down or refused us — show it instead of a misleading sign-in.
      if (statusCode !== 401) {
        const { t } = i18n.global;
        const forbidden = statusCode === 403;

        useErrorStore().setError({
          statusCode: statusCode ?? 500,
          title: forbidden ? t('errors.forbidden.title') : t('errors.server.title'),
          description: forbidden ? t('errors.forbidden.description') : t('errors.server.description'),
          critical: true,
        });

        return;
      }
    }
  }

  if (!auth.isSignedIn) {
    return {
      name: 'Login',
      query: to.fullPath === '/' ? {} : { redirect_uri: to.fullPath },
    };
  }
};
